/**
 * Trait Style Registry - React components for modal trait controls
 * modalTraitStyles[trait] = 'Style01' | 'Style02' | ... maps to these components
 * 涵蓋 GLOBAL TRAITS LIST 全部 27 個 traits
 */
import type { ComponentType } from 'react'
import type { TraitControlProps } from './types'
import { OnOffStyle01 } from './OnOffStyle01'
import { OnOffStyle02 } from './OnOffStyle02'
import { OnOffStyle03 } from './OnOffStyle03'
import { OnOffStyle04 } from './OnOffStyle04'
import { BrightnessStyle01 } from './BrightnessStyle01'
import { BrightnessStyle02 } from './BrightnessStyle02'
import { BrightnessStyle03 } from './BrightnessStyle03'
import { BrightnessStyle04 } from './BrightnessStyle04'
import { BrightnessStyle05 } from './BrightnessStyle05'
import { TemperatureSettingStyle03 } from './TemperatureSettingStyle03'
import { TemperatureSettingStyle05 } from './TemperatureSettingStyle05'
import { TemperatureSettingStyle06 } from './TemperatureSettingStyle06'
import { FanSpeedStyle01 } from './FanSpeedStyle01'
import { FanSpeedStyle02 } from './FanSpeedStyle02'
import { ThermostatSetModeStyle01 } from './ThermostatSetModeStyle01'
import { ThermostatSetModeStyle02 } from './ThermostatSetModeStyle02'
import { ThermostatSetModeStyle03 } from './ThermostatSetModeStyle03'
import { ThermostatSetModeStyle04 } from './ThermostatSetModeStyle04'
import { CustomTogglesStyle01 } from './CustomTogglesStyle01'
import { CustomTogglesStyle02 } from './CustomTogglesStyle02'
import { ColorSettingStyle01 } from './ColorSettingStyle01'
import { ColorSettingStyle02 } from './ColorSettingStyle02'
import { ColorSettingStyle03 } from './ColorSettingStyle03'
import { ColorSettingStyle04 } from './ColorSettingStyle04'
import { ColorTemperatureStyle01 } from './ColorTemperatureStyle01'
import { SensorStyle01 } from './SensorStyle01'
import { DetectorStyle01 } from './DetectorStyle01'
import { SceneStyle01 } from './SceneStyle01'
import { ZoneAlarmStyle01 } from './ZoneAlarmStyle01'
import { ZoneAlarmStyle02 } from './ZoneAlarmStyle02'
import { ReportStateStyle01 } from './ReportStateStyle01'
import { MatterControllerStyle01 } from './MatterControllerStyle01'
import { InternetAccessControlStyle01 } from './InternetAccessControlStyle01'
import { SmartConnectStyle01 } from './SmartConnectStyle01'
import { UloTMatterBridgeStyle01 } from './UloTMatterBridgeStyle01'
import { WifiApStyle01 } from './WifiApStyle01'
import { GlcControllerOnOffStyle01 } from './GlcControllerOnOffStyle01'
import { GlcWorkerOnOffStyle01 } from './GlcWorkerOnOffStyle01'
import { CustomModesStyle01 } from './CustomModesStyle01'
import { CustomModesStyle02 } from './CustomModesStyle02'
import { HumiditySettingStyle01 } from './HumiditySettingStyle01'
import { OpenCloseStyle01 } from './OpenCloseStyle01'
import { OpenCloseStyle02 } from './OpenCloseStyle02'
import { OpenCloseStyle03 } from './OpenCloseStyle03'
import { ModesStyle01 } from './ModesStyle01'
import { ModesStyle02 } from './ModesStyle02'
import { ModesStyle03 } from './ModesStyle03'
import { ModesStyle04 } from './ModesStyle04'
import { OccupancyStyle01 } from './OccupancyStyle01'
import { OccupancyStyle02 } from './OccupancyStyle02'
import { CoordinatorStyle01 } from './CoordinatorStyle01'
import { CoordinatorStyle02 } from './CoordinatorStyle02'
import { CustomModesStyle03 } from './CustomModesStyle03'

export type TraitStyleRegistry = Record<string, Record<string, ComponentType<TraitControlProps>>>

/** Trait style descriptions - all in English */
export const TRAIT_STYLE_DESCRIPTIONS: Record<string, Record<string, string>> = {
  Brightness: {
    Style01: 'Yellow slider + percentage (0-100%)',
    Style02: 'Slider style: value only',
    Style03: 'Vertical slider: lightbulb icon',
    Style04: 'Stitch: light_mode + gray→white slider, Dim/Brilliant',
    Style05: 'Stitch v2: gold theme + light_mode + Dim/Brilliant',
  },
  OnOff: {
    Style01: 'Stitch: Power State + large power button',
    Style02: 'Stitch v2: orange accent + large power button',
    Style03: 'Stitch v1: 垂直膠囊滑桿 + device type icon',
    Style04: 'Stitch v2: 垂直膠囊滑桿 + device type icon',
  },
  OnOffToggle: {
    Style01: 'Stitch: Power State + large power button',
    Style02: 'Stitch v2: orange accent + large power button',
    Style03: 'Stitch v1: 垂直膠囊滑桿 + device type icon',
    Style04: 'Stitch v2: 垂直膠囊滑桿 + device type icon',
  },
  StartStop: {
    Style01: 'Stitch: Start / Stop + large power button',
  },
  FanSpeed: {
    Style01: 'Stitch v1: mode_fan + 4-segment Low/Med/High/Turbo',
    Style02: 'Stitch v2: gold theme + mode_fan + 4-segment',
  },
  TemperatureSetting: {
    Style03: 'Stitch: thermostat + circular 22°C + sensor temperature row',
    Style05: 'Stitch: thermostat + circular 22°C + +/- buttons',
    Style06: 'Stitch v2: gold/orange + circular 22°C + clip arc',
  },
  TemperatureControl: {
    Style03: 'Stitch: thermostat + circular 22°C + sensor temperature row',
    Style05: 'Stitch: thermostat + circular 22°C + +/- buttons',
    Style06: 'Stitch v2: gold/orange + circular 22°C + clip arc',
  },
  ThermostatSetMode: {
    Style01: 'Mode selector: Cool / Heat / Dry / Fan / Auto',
    Style02: 'Chip menu',
    Style03: 'Vertical menu',
    Style04: 'Arc options',
  },
  CustomToggles: {
    Style01: 'Stitch v1: glass + toggle + eco/sleep/turbo buttons',
    Style02: 'Stitch v2: gold theme + toggle buttons',
  },
  CustomModes: {
    Style01: 'Stitch v1: glass + tune + mode buttons',
    Style02: 'Stitch v2: gold theme + mode buttons',
    Style03: 'Smart Blind mobile: preset chips (25/50/75/sun)',
  },
  HumiditySetting: { Style01: 'Slider style: humidity 30-80%' },
  OpenClose: {
    Style01: 'Stitch v1: glass + icon + name + slider (CLOSE ↔ OPEN)',
    Style02: 'Stitch v2: gold theme + icon + name + slider (CLOSE ↔ OPEN)',
    Style03: 'Smart Blind mobile: Position slider',
  },
  Modes: {
    Style01: 'Button style: mode selection',
    Style02: 'Stitch v1: glass + tune + mode buttons',
    Style03: 'Stitch v2: gold theme + mode buttons',
    Style04: 'Smart Blind mobile: Open/Pause/Close buttons',
  },
  ColorSetting: {
    Style01: 'Stitch v1: color temp only (wb_sunny + warm→cool + 5 swatches)',
    Style02: 'Stitch v2: color temp only, gold theme',
    Style03: 'Stitch v1: palette + spectrum slider + 5 preset swatches',
    Style04: 'Stitch v2: gold theme + palette + 5 swatches',
  },
  ColorTemperature: { Style01: 'Slider style: 2700-6500K color temp' },
  Coordinator: {
    Style01: 'Stitch v1: glass + hub + status',
    Style02: 'Stitch v2: gold theme + status',
  },
  Detector: { Style01: 'Status display: Detected / Not detected' },
  GlcControllerOnOff: { Style01: 'Button style: On / Off' },
  GlcWorkerOnOff: { Style01: 'Button style: On / Off' },
  InternetAccessControl: { Style01: 'Button style: On / Off' },
  MatterController: { Style01: 'Status display: Connected / Disconnected' },
  Occupancy: {
    Style01: 'Stitch v1: card + 光暈光點(Detected)/icon(無event)',
    Style02: 'Stitch v2: gold theme + 光暈光點/icon',
  },
  ReportState: { Style01: 'Read-only state report' },
  Scene: { Style01: 'Scene trigger button' },
  Sensor: { Style01: 'Read-only sensor value display' },
  SmartConnect: { Style01: 'Connect button' },
  UloTMatterBridge: { Style01: 'Bridge status: Connected / Disconnected' },
  WifiAp: { Style01: 'Status display: Enabled / Disabled' },
  ZoneAlarm: {
    Style01: 'Stitch v1: card + 光暈光點(Armed)/icon(Disarmed)',
    Style02: 'Stitch v2: gold theme + 光暈光點/icon',
  },
}

export const TRAIT_STYLE_REGISTRY: TraitStyleRegistry = {
  Brightness: {
    Style01: BrightnessStyle01,
    Style02: BrightnessStyle02,
    Style03: BrightnessStyle03,
    Style04: BrightnessStyle04,
    Style05: BrightnessStyle05,
  },
  ColorSetting: {
    Style01: ColorSettingStyle01,
    Style02: ColorSettingStyle02,
    Style03: ColorSettingStyle03,
    Style04: ColorSettingStyle04,
  },
  ColorTemperature: { Style01: ColorTemperatureStyle01 },
  Coordinator: {
    Style01: CoordinatorStyle01,
    Style02: CoordinatorStyle02,
  },
  CustomModes: {
    Style01: CustomModesStyle01,
    Style02: CustomModesStyle02,
    Style03: CustomModesStyle03,
  },
  CustomToggles: {
    Style01: CustomTogglesStyle01,
    Style02: CustomTogglesStyle02,
  },
  Detector: { Style01: DetectorStyle01 },
  FanSpeed: {
    Style01: FanSpeedStyle01,
    Style02: FanSpeedStyle02,
  },
  GlcControllerOnOff: { Style01: GlcControllerOnOffStyle01 },
  GlcWorkerOnOff: { Style01: GlcWorkerOnOffStyle01 },
  HumiditySetting: { Style01: HumiditySettingStyle01 },
  InternetAccessControl: { Style01: InternetAccessControlStyle01 },
  MatterController: { Style01: MatterControllerStyle01 },
  Modes: {
    Style01: ModesStyle01,
    Style02: ModesStyle02,
    Style03: ModesStyle03,
    Style04: ModesStyle04,
  },
  Occupancy: {
    Style01: OccupancyStyle01,
    Style02: OccupancyStyle02,
  },
  OnOff: {
    Style01: OnOffStyle01,
    Style02: OnOffStyle02,
    Style03: OnOffStyle03,
    Style04: OnOffStyle04,
  },
  OnOffToggle: {
    Style01: OnOffStyle01,
    Style02: OnOffStyle02,
    Style03: OnOffStyle03,
    Style04: OnOffStyle04,
  },
  StartStop: {
    Style01: OnOffStyle01,
  },
  OpenClose: {
    Style01: OpenCloseStyle01,
    Style02: OpenCloseStyle02,
    Style03: OpenCloseStyle03,
  },
  ReportState: { Style01: ReportStateStyle01 },
  Scene: { Style01: SceneStyle01 },
  Sensor: { Style01: SensorStyle01 },
  SmartConnect: { Style01: SmartConnectStyle01 },
  TemperatureControl: {
    Style03: TemperatureSettingStyle03,
    Style05: TemperatureSettingStyle05,
    Style06: TemperatureSettingStyle06,
  },
  TemperatureSetting: {
    Style03: TemperatureSettingStyle03,
    Style05: TemperatureSettingStyle05,
    Style06: TemperatureSettingStyle06,
  },
  ThermostatSetMode: {
    Style01: ThermostatSetModeStyle01,
    Style02: ThermostatSetModeStyle02,
    Style03: ThermostatSetModeStyle03,
    Style04: ThermostatSetModeStyle04,
  },
  UloTMatterBridge: { Style01: UloTMatterBridgeStyle01 },
  WifiAp: { Style01: WifiApStyle01 },
  ZoneAlarm: {
    Style01: ZoneAlarmStyle01,
    Style02: ZoneAlarmStyle02,
  },
}

export function getStyleDescription(trait: string, styleId: string): string | undefined {
  return TRAIT_STYLE_DESCRIPTIONS[trait]?.[styleId]
}

export function getTraitStyleComponent(
  trait: string,
  styleId: string
): ComponentType<TraitControlProps> | null {
  const normalizedTrait = trait?.trim() || ''
  const normalizedStyle = styleId?.trim() || 'Style01'
  const traitMap = TRAIT_STYLE_REGISTRY[normalizedTrait]
  if (!traitMap) return null
  return traitMap[normalizedStyle] ?? traitMap.Style01 ?? null
}

export function listTraitStyles(): Array<{ trait: string; styleId: string; name: string }> {
  const result: Array<{ trait: string; styleId: string; name: string }> = []
  for (const [trait, styles] of Object.entries(TRAIT_STYLE_REGISTRY)) {
    for (const styleId of Object.keys(styles)) {
      result.push({ trait, styleId, name: `${trait} ${styleId}` })
    }
  }
  return result
}

export function getStyleIdsForTrait(trait: string): string[] {
  const traitMap = TRAIT_STYLE_REGISTRY[trait?.trim() || '']
  return traitMap ? Object.keys(traitMap) : []
}

/** 所有有 Component Style 的 traits（開發者用，不依賴 device registry） */
export function listTraitsWithStyles(): string[] {
  return Object.keys(TRAIT_STYLE_REGISTRY).sort((a, b) => a.localeCompare(b))
}
